/**
 * Orquestador de impresión en el frontend.
 * Genera el contenido del ticket, lo guarda en el servidor y delega la
 * impresión al agente local cuando está disponible. También expone helpers
 * reutilizables para el flujo desde los componentes React.
 */
import ThermalPrinter from './thermalPrinter.js';
import { saveTicketToServer, generateTicketFilename, saveTicketFile } from './ticketUtils.js';
import agentService from './agentService.js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const COOKIE_PRINTER_NUMBER = 'printerNumber';

function getCookie(name) {
  const pattern = `(?:^|; )${name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, '\\$1')}=([^;]*)`;
  const match = document.cookie.match(new RegExp(pattern));
  return match ? decodeURIComponent(match[1]) : null;
}

function setSessionCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/;`;
}

/**
 * Servicio principal de impresión de tickets
 * Maneja la impresión directa y el guardado de archivos
 */
class PrinterService {
  constructor() {
    this.printer = new ThermalPrinter();
    this.isConnected = false;
  }

  /**
   * Función principal para imprimir un ticket
   * @param {Object} params - Parámetros del ticket
   * @param {Object} params.transaccionData - Datos de la transacción
   * @param {Array} params.itemsTransaccion - Items de la transacción
   * @param {Object} params.entidadParaFactura - Datos del cliente
   * @param {Object} params.parametrosGlobales - Parámetros globales del negocio
   * @returns {Promise<Object>} Resultado de la impresión
   */
  async printTicket({ transaccionData, itemsTransaccion, entidadParaFactura, parametrosGlobales }) {
    const result = {
      success: false,
      message: '',
      ticketSaved: false,
      printed: false
    };

    try {
      console.log('🖨️ Iniciando proceso de impresión de ticket...');

      // 1. Preparar datos del ticket
      const ticketData = this.prepareTicketData({
        transaccionData,
        itemsTransaccion,
        entidadParaFactura,
        parametrosGlobales
      });

      console.log('📋 Datos del ticket preparados:', ticketData);

      // 2. Generar contenido del ticket para guardado
      const ticketContent = this.generateTicketContent(ticketData);

      // 3. Guardar ticket en el servidor (siempre se hace)
      try {
        const serverResult = await saveTicketToServer(ticketData.sale.id, ticketContent);
        if (serverResult) {
          result.ticketSaved = true;
          console.log('✅ Ticket guardado en servidor');
        }
      } catch (error) {
        console.warn('⚠️ Error guardando ticket en servidor:', error);
      }

      // 4. Intentar imprimir con el agente local
      try {
        const agentAvailable = await agentService.checkAgentAvailability();
        
        if (agentAvailable) {
          let printerNumber = getCookie(COOKIE_PRINTER_NUMBER);
          if (!printerNumber) {
            const input = window.prompt('Número de impresora donde desea imprimir?');
            if (!input) {
              throw new Error('Impresora no configurada');
            }
            printerNumber = input.trim();
            setSessionCookie(COOKIE_PRINTER_NUMBER, printerNumber);
          }

          const tenant = getCookie('tenant') || agentService.getTenantFromCookies() || 'unknown';
          console.log(`🧾 Metadatos de impresión -> tenant=${tenant || '-'} impresora=${printerNumber}`);

          // Usar agente local para imprimir
          const agentResponse = await agentService.printTicket({
            ...ticketData,
            tenant,
            printerNumber
          });
          result.printed = true;
          result.success = true;
          result.message = agentResponse?.data?.message || 'Ticket impreso correctamente via agente local';
          console.log('✅ Ticket impreso via agente local');
        } else {
          console.warn('⚠️ Agente no disponible: se omite impresión automática');
          result.message = '';
        }
      } catch (agentError) {
        console.warn('⚠️ Error con agente:', agentError);
        result.message = '';
      }

    } catch (error) {
      console.error('❌ Error en el proceso de impresión:', error);
      result.message = `Error de impresión: ${error.message}`;
    }

    return result;
  }

  /**
   * Prepara los datos del ticket en el formato requerido
   * @param {Object} params - Parámetros de entrada
   * @returns {Object} Datos del ticket formateados
   */
  prepareTicketData({ transaccionData, itemsTransaccion, entidadParaFactura, parametrosGlobales }) {
    // Preparar datos del negocio usando parámetros globales
    const business = {
      name: parametrosGlobales?.afipsdk_razon_social || 'Mi Negocio',
      address: parametrosGlobales?.afipsdk_domicilio || null,
      phone: parametrosGlobales?.afipsdk_telefono || null,
      cuit: parametrosGlobales?.afipsdk_cuit || null,
      condicionIva: parametrosGlobales?.afipsdk_condicion_iva || 'RESPONSABLE INSCRIPTO'
    };

    // Preparar datos del cliente
    const customer = {
      name: entidadParaFactura?.descripcion || 'Consumidor Final',
      numeroDoc: entidadParaFactura?.dniCuitCuil || '0'
    };

    // Preparar items con cálculo de totales
    const items = itemsTransaccion.map(item => ({
      cantidad: item.cantidad,
      descripcion: item.descripcion || 'Sin descripción',
      precioUnitario: item.precio,
      total: item.precio * item.cantidad
    }));

    // Calcular total sumando todos los items
    const calculatedTotal = items.reduce((sum, item) => sum + item.total, 0);

    // Preparar datos de la venta
    const sale = {
      id: transaccionData.id,
      dateISO: transaccionData.fecha,
      items: items,
      total: calculatedTotal || transaccionData.montoTotal || 0
    };

    return {
      business,
      sale,
      customer,
      footerMessage: 'Gracias por su compra'
    };
  }

  /**
   * Genera el contenido del ticket en formato texto plano
   * @param {Object} ticketData - Datos del ticket
   * @returns {string} Contenido del ticket
   */
  generateTicketContent(ticketData) {
    const { business, sale, customer, footerMessage } = ticketData;
    
    let content = '';
    
    // Encabezado del negocio
    content += `${this.cleanText(business.name) || 'Mi Negocio'}\n`;
    if (business.address) content += `${this.cleanText(business.address)}\n`;
    if (business.phone) content += `Tel: ${this.cleanText(business.phone)}\n`;
    if (business.cuit) content += `C.U.I.T.: ${this.cleanText(business.cuit)}\n`;
    if (business.condicionIva) content += `${this.cleanText(business.condicionIva)}\n`;
    
    content += `${this.generateLine()}\n`;
    content += `Ticket Nro: ${sale.id || '-'}\n`;
    content += `Fecha/Hora: ${this.formatDate(sale.dateISO)}\n`;
    
    // Información del cliente
    if (customer && (customer.name || customer.numeroDoc)) {
      content += `${this.generateLine()}\n`;
      if (customer.name && customer.name !== 'Consumidor Final') {
        content += `Cliente: ${this.cleanText(customer.name)}\n`;
      } else {
        content += 'A CONSUMIDOR FINAL\n';
      }
      if (customer.numeroDoc && customer.numeroDoc !== '0') {
        content += `DNI/CUIT/CUIL: ${customer.numeroDoc}\n`;
      }
    } else {
      content += `${this.generateLine()}\nA CONSUMIDOR FINAL\n`;
    }
    
    // Detalle de items
    if (sale.items && sale.items.length > 0) {
      content += `${this.generateLine()}\n`;
      content += 'DETALLE DE PRODUCTOS\n';
      content += `${this.generateLine()}\n`;
      content += `${this.padRight('Cant', 4)} ${this.padRight('Descripcion', 17)} ${this.padLeft('P.Unit', 10)}${this.padLeft('Total', 11)}\n`;
      
      sale.items.forEach(item => {
        const total = (item.precioUnitario || 0) * (item.cantidad || 0);
        content += `${this.formatItemLine({
          cantidad: item.cantidad,
          descripcion: item.descripcion,
          precioUnitario: item.precioUnitario,
          total: total
        })}\n`;
      });
      
      content += `${this.generateLine()}\n`;
    }

    // Total
    content += `TOTAL: ${this.formatCurrency(sale.total || 0)}\n`;
    content += `${this.generateLine()}\n`;
    content += `${footerMessage || 'Gracias por su compra'}\n`;
    
    // Saltos de línea para facilitar el corte del papel
    content += '\n\n\n\n\n\n\n\n\n\n';

    return content;
  }

  /**
   * Solicita confirmación del usuario para imprimir
   * @returns {Promise<boolean>} true si el usuario confirma
   */
  async requestUserPermission() {
    return new Promise((resolve) => {
      const confirmed = window.confirm(
        '¿Desea imprimir el ticket en la impresora Hasar P-HAS-181?\n\n' +
        'Esto requerirá acceso a la impresora USB conectada a su equipo.'
      );
      resolve(confirmed);
    });
  }

  /**
   * Conecta con la impresora térmica
   * @returns {Promise<boolean>} true si se conecta exitosamente
   */
  async connectToPrinter() {
    try {
      const connected = await this.printer.connect();
      this.isConnected = connected;
      return connected;
    } catch (error) {
      console.error('❌ Error conectando con impresora:', error);
      return false;
    }
  }

  /**
   * Desconecta de la impresora
   */
  async disconnectFromPrinter() {
    try {
      if (this.isConnected) {
        await this.printer.disconnect();
        this.isConnected = false;
      }
    } catch (error) {
      console.error('❌ Error desconectando de impresora:', error);
    }
  }

  /**
   * Funciones de utilidad (reutilizadas del backend)
   */

  cleanText(text) {
    if (!text) return '';
    
    return String(text)
      .replace(/á/g, 'a').replace(/Á/g, 'A')
      .replace(/é/g, 'e').replace(/É/g, 'E')
      .replace(/í/g, 'i').replace(/Í/g, 'I')
      .replace(/ó/g, 'o').replace(/Ó/g, 'O')
      .replace(/ú/g, 'u').replace(/Ú/g, 'U')
      .replace(/ñ/g, 'n').replace(/Ñ/g, 'N')
      .replace(/ü/g, 'u').replace(/Ü/g, 'U')
      .replace(/°/g, 'o')
      .replace(/¿/g, '?')
      .replace(/¡/g, '!')
      .replace(/[^\x20-\x7E]/g, '?');
  }

  formatDate(dateISO) {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    try {
      const parsed = dateISO ? dayjs.utc(dateISO) : dayjs();
      const zonedDate = parsed.isValid() ? parsed.tz(zone) : dayjs().tz(zone);

      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: zone,
      }).format(zonedDate.toDate());
    } catch (error) {
      return new Intl.DateTimeFormat('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: zone,
      }).format(new Date());
    }
  }

  generateLine() {
    return '-'.repeat(42);
  }

  formatCurrency(value) {
    if (isNaN(value) || value === null || value === undefined) {
      return '$ 0,00';
    }
    const formatted = new Intl.NumberFormat('es-AR', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
    return `$ ${formatted}`;
  }

  formatCurrencyWithoutSymbol(value) {
    if (isNaN(value) || value === null || value === undefined) {
      return '0,00';
    }
    return new Intl.NumberFormat('es-AR', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  padLeft(str, width) {
    return str.length >= width ? str.substring(0, width) : ' '.repeat(width - str.length) + str;
  }

  padRight(str, width) {
    return str.length >= width ? str.substring(0, width) : str + ' '.repeat(width - str.length);
  }

  formatItemLine({ cantidad, descripcion, precioUnitario, total }) {
    const colCant = 4;
    const colDesc = 17;
    const colUnit = 10;
    const colTotal = 11;

    const cantStr = String(cantidad || '0');
    const descStr = this.cleanText(descripcion || 'Sin descripción');
    const unitStr = this.formatCurrencyWithoutSymbol(precioUnitario || 0);
    const totalStr = this.formatCurrencyWithoutSymbol(total || 0);

    const cantF = this.padLeft(cantStr, colCant);
    const descF = this.padRight(descStr, colDesc);
    const unitF = this.padLeft(unitStr, colUnit);
    const totalF = this.padLeft(totalStr, colTotal);

    return `${cantF} ${descF} ${unitF}${totalF}`;
  }

  /**
   * Ofrece descarga del ticket como fallback
   * @param {string} ticketContent - Contenido del ticket
   * @param {string|number} saleId - ID de la venta
   */
  offerDownloadFallback(ticketContent, saleId) {
    try {
      const filename = generateTicketFilename(saleId);
      const downloaded = saveTicketFile(ticketContent, filename);
      
      if (downloaded) {
        console.log('📥 Ticket descargado como fallback');
        // Mostrar mensaje al usuario
        setTimeout(() => {
          alert(
            'No se pudo imprimir directamente.\n\n' +
            'El ticket se ha descargado a su carpeta de Descargas.\n' +
            'Puede imprimirlo manualmente desde allí.'
          );
        }, 1000);
      }
    } catch (error) {
      console.error('❌ Error en fallback de descarga:', error);
    }
  }
}

// Instancia singleton del servicio
const printerService = new PrinterService();

export default printerService;
